import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logo from "@/assets/quirky-french-logo.webp";

const Auth = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const { signUp, signIn, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !firstName || !lastName) return;
    setLoading(true);
    await signUp(email, password, firstName, lastName);
    setLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    await signIn(email, password);
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success(t("auth.resetLinkSent"));
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/[0.03] rounded-full -translate-y-1/2 translate-x-1/3 blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/[0.03] rounded-full translate-y-1/3 -translate-x-1/3 blur-[100px]" />
        </div>

        <Card className="w-full max-w-sm relative z-10 shadow-elevated border-border/50">
          <CardHeader className="text-center pb-2 pt-8">
            <a href="https://lecoinetude.quirkyfrench.com" className="inline-block mx-auto mb-5 hover:opacity-80 transition-opacity">
              <img src={logo} alt="Quirky French" className="h-10" />
            </a>
            <CardTitle className="text-xl">{t("auth.resetPassword")}</CardTitle>
            <CardDescription className="text-sm">{t("auth.resetPasswordDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="pb-8">
            <form onSubmit={handleForgotPassword} className="space-y-3.5">
              <div className="space-y-1.5">
                <Label htmlFor="reset-email" className="text-xs">{t("auth.email")}</Label>
                <Input id="reset-email" type="email" placeholder="your@email.com" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required className="h-9 text-sm" />
              </div>
              <Button type="submit" className="w-full h-9 text-sm rounded-full" disabled={loading}>
                {loading ? t("auth.sending") : t("auth.sendResetLink")}
              </Button>
              <Button type="button" variant="ghost" className="w-full h-9 text-sm" onClick={() => setShowForgotPassword(false)}>
                {t("auth.backToSignIn")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Subtle background accents */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/[0.03] rounded-full -translate-y-1/2 translate-x-1/3 blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/[0.03] rounded-full translate-y-1/3 -translate-x-1/3 blur-[100px]" />
      </div>
      
      <Card className="w-full max-w-sm relative z-10 shadow-elevated border-border/50">
        <CardHeader className="text-center pb-2 pt-8">
          <a href="https://lecoinetude.quirkyfrench.com" className="inline-block mx-auto mb-5 hover:opacity-80 transition-opacity">
            <img src={logo} alt="Quirky French" className="h-10" />
          </a>
          <CardTitle className="text-xl">{t("auth.welcome")}</CardTitle>
          <CardDescription className="text-sm">{t("auth.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="pb-8">
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-5 h-9">
              <TabsTrigger value="signin" className="text-xs">{t("auth.signIn")}</TabsTrigger>
              <TabsTrigger value="signup" className="text-xs">{t("auth.signUp")}</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-3.5">
                <div className="space-y-1.5">
                  <Label htmlFor="signin-email" className="text-xs">{t("auth.email")}</Label>
                  <Input id="signin-email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-9 text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signin-password" className="text-xs">{t("auth.password")}</Label>
                  <Input id="signin-password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-9 text-sm" />
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-primary hover:underline"
                  >
                    {t("auth.forgotPassword")}
                  </button>
                </div>
                <Button type="submit" className="w-full h-9 text-sm rounded-full" disabled={loading}>
                  {loading ? t("auth.signingIn") : t("auth.signIn")}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-3.5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="signup-firstname" className="text-xs">{t("auth.firstName")}</Label>
                    <Input id="signup-firstname" type="text" placeholder="Jean" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="signup-lastname" className="text-xs">{t("auth.lastName")}</Label>
                    <Input id="signup-lastname" type="text" placeholder="Dupont" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="h-9 text-sm" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-email" className="text-xs">{t("auth.email")}</Label>
                  <Input id="signup-email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-9 text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-password" className="text-xs">{t("auth.password")}</Label>
                  <Input id="signup-password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="h-9 text-sm" />
                </div>
                <Button type="submit" className="w-full h-9 text-sm rounded-full" disabled={loading}>
                  {loading ? t("auth.creatingAccount") : t("auth.createAccount")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
